import { FC } from 'react';
import { useAsync, useDesign, useServices } from '@app/hooks';
import { AudioType, DesignLanguage } from '@app/types';

export type LanguageLabelProps = {
  /** Whether the language name should be displayed @default true. */
  includeLanguage?: boolean;
  /** Whether text to speech voice label should be displayed @default false. */
  includeTts?: boolean;
  /** Whether voice talent label should be displayed @default false. */
  includeVt?: boolean;
  /** Language to pull codes from and display labels for. */
  language: DesignLanguage;
};

/** Resolved label segments for a language / VT / TTS combination. */
export function useLanguageParts({
  includeLanguage = true,
  includeTts = false,
  includeVt = false,
  language
}: LanguageLabelProps): string[] | null {
  const {
    language: languageCode,
    ttsFallBackVoice: ttsVoiceCode,
    voice: ttpVoiceCode
  } = language;

  const serialized = useDesign();
  const { workflowService } = useServices();
  const [languages] = useAsync(() => {
    return workflowService.getLanguages();
  }, [serialized]);
  const [ttpVoices] = useAsync(() => {
    return workflowService.getVoices(AudioType.ttp, languageCode, serialized);
  }, [languageCode, serialized]);
  const [ttsVoices] = useAsync(() => {
    return workflowService.getVoices(AudioType.tts, languageCode, serialized);
  }, [languageCode, serialized]);
  const languageLabel = languages?.find(({ value }) => value === languageCode)?.label;
  const ttpLabel = (
    ttpVoices?.find(({ value }) => value === ttpVoiceCode)?.label
    ?? (ttpVoiceCode || null)
  );
  const ttsLabel = (
    ttsVoices?.find(({ value }) => value === ttsVoiceCode)?.label
    ?? (ttsVoiceCode || null)
  );

  if (includeLanguage && !languageLabel) {
    return null;
  }

  const parts: string[] = (includeLanguage && languageLabel) ? [languageLabel] : [];
  if (
    includeVt
    && includeTts
    && ttpLabel
    && ttsLabel
    && ttpLabel === ttsLabel
  ) {
    parts.push(`${ttpLabel} (VT + TTS)`);
  } else {
    if (includeVt && ttpLabel) {
      parts.push(`${ttpLabel} (VT)`);
    }
    if (includeTts && ttsLabel) {
      parts.push(`${ttsLabel} (TTS)`);
    }
  }

  return (parts.length < 1) ? null : parts;
}

/** Label for a language and its selected voices. */
export const LanguageLabel: FC<LanguageLabelProps> = (props) => {
  const parts = useLanguageParts(props);

  return (!parts) ? null : (
    <>
      {parts.map((text, i) => {
        return <span key={i}>{(i === 0) ? text : ` ${text}`}</span>;
      })}
    </>
  );
};
